import React, { Suspense } from 'react'
import './App.css'
import { Provider } from 'react-redux'
import store, { persistor } from './store'
import { PersistGate } from 'redux-persist/integration/react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { QueryClientProvider, QueryErrorResetBoundary } from 'react-query'
import { Toaster } from 'react-hot-toast'
import loadable from '@loadable/component'
import { ErrorBoundary } from 'react-error-boundary'
import { ReactComponent as SecureLinkExpiredIcon } from './images/icons/icon_secure_link_expired.svg'

/**
 * Onboarding
 */

const Onboarding = loadable(() => import('./pages/Onboarding/Onboarding'))

const Layout = loadable(() => import('./components/Layout/Layout'))
const RequireAuth = loadable(() => import('./components/RequireAuth'))
const ModalHost = loadable(() => import('./components/ModalHost'))
const ActiveBreakpoint = loadable(() => import('./components/ActiveBreakpoint'))
const LoadingComponent = loadable(() =>
  import('@demandlocal/components').then((c) => ({ default: c.LoadingComponent })),
)
import { initGA4 } from './lib/utils/ga4'
import { useDealership } from './store/zustand/dealershipStore'
import { queryClient } from './api/queryClient'
import Audiences from './pages/Dashboard/Dealer/Audiences/Audiences'
import DealershipAgencyRedirector from './components/RouterMiddlewares/DealershipAgencyRedirect'

const ScrollToTop = loadable(() => import('./components/ScrollToTop'))
const AgencyOverview = loadable(() => import('./pages/Dashboard/Agency/AgencyOverview'))
const AgencyDealerships = loadable(
  () => import('./pages/Dashboard/Agency/AgencyDealerships'),
)
const AgencyCampaigns = loadable(
  () => import('./pages/Dashboard/Agency/Campaigns/AgencyCampaigns'),
)
const Invoices = loadable(() => import('./pages/Dashboard/Agency/Invoices'))
const InvoiceHistory = loadable(() => import('./pages/Dashboard/Agency/InvoiceHistory'))
const InvoiceDetails = loadable(() => import('./pages/Dashboard/Agency/InvoiceDetails'))
const CheckSuperAdminAccess = loadable(
  () => import('./components/RouterMiddlewares/CheckSuperAdminAccess'),
)
const AgencyUsers = loadable(() => import('./pages/Dashboard/Agency/AgencyUsers'))
const AgencyExports = loadable(() => import('./pages/Dashboard/Agency/AgencyExports'))

// const CreateNewCampaignForm = loadable(
//   () => import('./forms/InventoryCampaign/NewCampaign/CreateNewCampaignForm'),
// )
const AgencyUserEdit = loadable(() => import('./forms/Agency/Users/AgencyEditUser'))
const AgencyAddUser = loadable(() => import('./forms/Agency/Users/AgencyAddUser'))

const AdminUserEdit = loadable(() => import('./forms/Admin/User/AdminUserEdit'))
const AdminUserAdd = loadable(() => import('./forms/Admin/User/AdminUserAdd'))
const EditDealership = loadable(() => import('./forms/Admin/Dealership/EditDealership'))
const AgencyEditDealership = loadable(
  () => import('./forms/Agency/Dealership/EditDealership'),
)
const AddDealership = loadable(() => import('./forms/Admin/Dealership/AddDealership'))
const AdminIntegrations = loadable(() => import('./pages/Dashboard/Admin/Integrations'))
const AgencyReports = loadable(
  () => import('./pages/Dashboard/Agency/Reports/AgencyReports'),
)
const GenerateReports = loadable(() => import('./forms/Agency/GenerateReports'))
const AdminPartners = loadable(() => import('./pages/Dashboard/Admin/Partners'))
const AddAudience = loadable(() => import('./forms/AddAudience'))

const AgencyProfile = loadable(() => import('./pages/Dashboard/Agency/AgencyProfile'))
const AddCustomersForm = loadable(() => import('./forms/AddCustomers'))
const CheckAgencyAccess = loadable(
  () => import('./components/RouterMiddlewares/CheckAgencyAccess'),
)
const CheckDealershipAccess = loadable(
  () => import('./components/RouterMiddlewares/CheckDealershipAccess'),
)
const DefaultAgencyRedirect = loadable(
  () => import('./components/RouterMiddlewares/DefaultAgencyRedirect'),
)
const DefaultDealershipRedirect = loadable(
  () => import('./components/RouterMiddlewares/DefaultDealershipRedirect'),
)
const AuthForgot = loadable(() => import('./pages/Auth/Forgot'))
const AuthReset = loadable(() => import('./pages/Auth/Reset'))
const SecureUpload = loadable(() => import('./pages/SecureUpload/SecureUpload'))

const Auth = loadable(() => import('./pages/Auth'))
const AuthLogin = loadable(() => import('./pages/Auth/Login'))
const AuthSignup = loadable(() => import('./pages/Auth/Signup'))
const MultiFactorAuth = loadable(() => import('./pages/Auth/MultiFactorAuth'))
const DealerProfile = loadable(
  () => import('./pages/Dashboard/Dealer/Profile/DealerProfile'),
)
const TermsAndConditions = loadable(() => import('./pages/Auth/TermsAndConditions'))
const DealerCustomers = loadable(
  () => import('./pages/Dashboard/Dealer/Customers/Customers'),
)
const Vehicles = loadable(() => import('./pages/Dashboard/Dealer/Inventory/Vehicles'))

const Accounts = loadable(() => import('./pages/Dashboard/Agency/Accounts'))
const OAuthSuccess = loadable(() => import('./pages/OAuthSuccess'))
const Admin = loadable(() => import('./pages/Dashboard/Admin/Admin'))
const AdminUsers = loadable(() => import('./pages/Dashboard/Admin/Users'))
const AdminAgencies = loadable(() => import('./pages/Dashboard/Admin/Agencies'))
const AdminDealerships = loadable(() => import('./pages/Dashboard/Admin/Dealerships'))
const Campaigns = loadable(() => import('./pages/Dashboard/Dealer/Campaigns/Campaigns'))
const Reports = loadable(() => import('./pages/Dashboard/Dealer/Reports/Reports'))

const User = loadable(() => import('./pages/Dashboard/User/User'))

const Privacy = loadable(() => import('./pages/Landing/Privacy'))
const Pricing = loadable(() => import('./pages/Landing/Pricing'))
const Terms = loadable(() => import('./pages/Landing/Terms'))
const DPA = loadable(() => import('./pages/Landing/DPA'))

initGA4()

const StoreRedirect = (): JSX.Element => {
  const dealership = useDealership()

  if (dealership) {
    if (dealership.industry === 'automotive') return <Navigate to="dealership" replace />
    else return <Navigate to="account" replace />
  }

  return <Navigate to="dealership" replace />
}

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ModalHost>
            {process.env.NODE_ENV === 'development' && <ActiveBreakpoint />}
            <BrowserRouter>
              <Toaster />
              <ScrollToTop />
              <Routes>
                <Route path="/" element={<Navigate to={'/dashboard'} replace={true} />} />
                <Route path={'/pricing'} element={<Pricing />} />
                <Route path="/privacy-notice" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/data-processing-agreement" element={<DPA />} />
                <Route
                  path={'/secure-upload'}
                  element={
                    <QueryErrorResetBoundary>
                      {({ reset }) => (
                        <ErrorBoundary
                          fallbackRender={({ error, resetErrorBoundary }) => (
                            <div
                              className={
                                'w-full h-screen flex flex-1 justify-center items-center flex-col gap-8'
                              }>
                              <SecureLinkExpiredIcon />
                              <div className={'text-3xl font-bold text-primary-xiketic'}>
                                Your secure link has been used or has expired.
                              </div>
                            </div>
                          )}
                          onReset={reset}>
                          <Suspense
                            fallback={
                              <div className={'w-full h-screen mx-auto relative'}>
                                <LoadingComponent title={'Checking your token...'} />
                              </div>
                            }>
                            <SecureUpload />
                          </Suspense>
                        </ErrorBoundary>
                      )}
                    </QueryErrorResetBoundary>
                  }
                />
                <Route path="auth/*" element={<Auth />}>
                  <Route path={'login'} element={<AuthLogin />} />
                  <Route path={'sign-up'} element={<AuthSignup />} />
                  <Route path={'terms-and-conditions'} element={<TermsAndConditions />} />
                  <Route path={'mfa/*'} element={<MultiFactorAuth />} />
                  <Route path={'forgot-password'} element={<AuthForgot />} />
                  <Route path={'reset-password/:token'} element={<AuthReset />} />
                </Route>
                <Route
                  path="dashboard/*"
                  element={
                    <RequireAuth>
                      <Layout />
                    </RequireAuth>
                  }>
                  <Route
                    path="dealership/:dealership_id/*"
                    element={<DealershipAgencyRedirector />}
                  />
                  <Route path="onboarding/*" element={<Onboarding />} />
                  <Route path={'agency/*'}>
                    <Route path={':agency_id/*'} element={<CheckAgencyAccess />}>
                      <Route path={'profile/*'} element={<AgencyProfile />}>
                        <Route path="overview" element={<AgencyOverview />} />
                        <Route path={'clients/*'} element={<AgencyDealerships />}>
                          <Route
                            path={':dealership_id/edit/*'}
                            element={<AgencyEditDealership />}
                          />
                        </Route>
                        <Route path={'campaigns/*'} element={<AgencyCampaigns />} />
                        <Route path={'exports/*'} element={<AgencyExports />} />
                        <Route path={'users/*'} element={<AgencyUsers />}>
                          <Route path={':user_id/edit/*'} element={<AgencyUserEdit />} />
                          <Route path={'new/*'} element={<AgencyAddUser />} />
                        </Route>
                        <Route path={'invoices'} element={<Invoices />} />
                        <Route path="*" element={<Navigate to="overview" replace />} />
                        <Route path={'reports/*'} element={<AgencyReports />}>
                          <Route path={'generate'} element={<GenerateReports />} />
                        </Route>
                        <Route path={'*'} element={<CheckSuperAdminAccess />}>
                          <Route path={'invoice-history'} element={<InvoiceHistory />} />
                          <Route path={'invoice-details'} element={<InvoiceDetails />} />
                        </Route>
                      </Route>
                      <Route path={'profile/accounts/*'} element={<Accounts />} />

                      {['dealership/*', 'account/*'].map((path) => (
                        <Route path={path} key={path}>
                          <Route
                            path={':dealership_id/*'}
                            element={<CheckDealershipAccess />}>
                            <Route path={'profile/*'} element={<DealerProfile />}>
                              <Route
                                path={'create'}
                                element={
                                  <Navigate
                                    to={'../../performace/campaigns/list/create'}
                                  />
                                }
                              />
                              <Route
                                path={'sources/new/*'}
                                element={<AddCustomersForm />}
                              />
                              <Route path={'audience/new/*'} element={<AddAudience />} />
                            </Route>
                            <Route path={'customers/*'} element={<DealerCustomers />} />
                            <Route path={'audiences/*'} element={<Audiences />} />
                            <Route path={'inventory/*'} element={<Vehicles />} />
                            <Route path={'campaigns/*'} element={<Campaigns />} />
                            <Route path={'reports/*'} element={<Reports />} />
                            {/* <Route path={'accounts/*'} element={<Accounts />} /> */}
                            <Route path="*" element={<Navigate to="profile" replace />} />
                          </Route>
                          <Route path={'*'} element={<DefaultDealershipRedirect />} />
                        </Route>
                      ))}
                      <Route path="*" element={<StoreRedirect />} />
                    </Route>
                    <Route path={'*'} element={<DefaultAgencyRedirect />} />
                  </Route>
                  <Route path={'admin/*'} element={<Admin />}>
                    <Route path={'users/*'} element={<AdminUsers />}>
                      <Route path={':user_id/edit/*'} element={<AdminUserEdit />} />
                      <Route path={'new/*'} element={<AdminUserAdd />} />
                    </Route>
                    <Route path={'organizations'} element={<AdminAgencies />} />
                    <Route path={'dealerships/*'} element={<AdminDealerships />}>
                      <Route
                        path={':dealership_id/edit/*'}
                        element={<EditDealership />}
                      />
                      <Route path={'new/*'} element={<AddDealership />} />
                    </Route>
                    <Route path={'integrations/*'} element={<AdminIntegrations />} />
                    <Route path={'partners/*'} element={<AdminPartners />} />
                    <Route path="*" element={<Navigate to="users" replace />} />
                  </Route>
                  <Route path={'user/*'} element={<User />}>
                    <Route path="*" element={<Navigate to="profile" replace />} />
                  </Route>
                  <Route path={'oauth_success'} element={<OAuthSuccess />} />
                  <Route path="*" element={<Navigate to="agency" replace />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </ModalHost>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  )
}

export default App
